import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { Country } from '../types/country.model';
import { SharedService } from '../shared.service';
import { PrimaryFunction } from '../types/primary-function.model';
import { Filter } from '../types/filter.model';
import { BehaviorSubject, Observable } from 'rxjs';
import { CAREER_TYPES } from './../../careers/types/career-types.enum';
import { EVENT_TYPES } from '../../events/types/event-types.enum';
import { INITIATIVE_TYPES } from '../../initiatives/types/initiative-type.enum';
import { CARD_TYPES } from '../enums/type.enum';
import { SORT_TYPES } from '../enums/sort.enum';
import { LOCATION_TYPES } from '../enums/location.enum';

@Component({
  selector: 'mmb-web-app-top-level-filters',
  templateUrl: './top-level-filters.component.html',
  styleUrls: ['./top-level-filters.component.scss']
})
export class TopLevelFiltersComponent implements OnInit {
  @Input() entityType: string = 'generic';
  filtersForm: UntypedFormGroup;
  countries: Country[];
  currentCitiesSearch: any;
  functions: PrimaryFunction[];
  industries: PrimaryFunction[];
  careerTypes = CAREER_TYPES;
  eventTypes = EVENT_TYPES;
  initiativeTypes = INITIATIVE_TYPES;
  types = CARD_TYPES;
  locations = LOCATION_TYPES;
  sorts = SORT_TYPES;
  seniorityTypes: any;

  @Input()
  set filters(filter: BehaviorSubject<Filter>) {
    this._initFilter(filter.value);
  }
  @Output() filtersUpdated: EventEmitter<any> = new EventEmitter<any>();
  constructor(private fb: UntypedFormBuilder, private sharedService: SharedService) {
    this._initFilter();
  }

  ngOnInit() {
    // load all countries
    this.sharedService.getCountries('', 500).subscribe((countries) => {
      this.countries = countries;
    });

    // load all primary functions
    this.sharedService.getPrimaryFunctions().subscribe((functions) => {
      this.functions = functions;
    });

    // load all primary industries
    this.sharedService.getPrimaryIndustries().subscribe((industries) => {
      this.industries = industries;
    });

    this.sharedService.referenceTypes$.subscribe((value) => {
      if (value) {
        this.seniorityTypes = value.type_reference_career_role_level;
      }
    })

    // subscribe to form value changes
    this._subscribeToKeywordChanges();
    this._subscribeToCountryChanges();
    this._subscribeToFunctionChanges();
    this._subscribeToIndustryChanges();
    this._subscribeToCareerTypeChanges();
    this._subscribeToSeniorityTypeChanges();
    this._subscribeToEventTypeChanges();
    this._subscribeToInitiativeTypeChanges();
    this._subscribeToCurrentCityValueChanges();
  }

  /**
   * Returns placeholder for input
   */
  getPlaceholder() {
    if (this.entityType === 'career') {
      return 'Job search';
    } else if (this.entityType === 'event') {
      return 'Event search';
    } else if (this.entityType === 'experience') {
      return 'Experience search';
    }
    return 'Keyword Search';
  }

  /**
   * Initialize the filter
   */
  private _initFilter(filter?: Filter) {
    this.filtersForm = this.fb.group({
      keyword: filter && filter.keyword ? filter.keyword : '',
      country: filter && filter.country ? filter.country : '',
      city: filter && filter.city ? filter.city : '',
      function: filter && filter.function ? filter.function : '',
      industry: filter && filter.industry ? filter.industry : '',
      careerType: filter && filter.careerType ? filter.careerType : '',
      seniorityType: filter && filter.seniorityType ? filter.seniorityType : '',
      eventType: filter && filter.eventType ? filter.eventType : '',
      initiativeType: filter && filter.initiativeType ? filter.initiativeType : '',
      type: filter && filter.type ? filter.type : 'Unanswered questions',
      location: filter && filter.location ? filter.location : 'Worldwide',
      sort: filter && filter.sort ? filter.sort : 'Newest',
    });
  }

  /**
   * Subscribes to keyword changes
   */
  private _subscribeToKeywordChanges() {
    this.filtersForm.controls['keyword'].valueChanges.pipe(
      debounceTime(500),
      tap(val => {
        this.filtersUpdated.emit(this.filtersForm.value);
      })
    ).subscribe();
  }

  /**
   * Subscribes to country changes
   */
  private _subscribeToCountryChanges() {
    this.filtersForm.controls['country'].valueChanges.pipe(
      debounceTime(500),
      tap(val => this.filtersUpdated.emit(this.filtersForm.value)),
      switchMap(country => this.sharedService.getCitiesByCountry(country))
    ).subscribe(cities => {
      this.currentCitiesSearch = cities;
    });
  }

  /**
 * Subscribes to city changes
 */
  private _subscribeToCurrentCityValueChanges() {
    this.filtersForm.controls['city'].valueChanges.pipe(
      debounceTime(500),
      tap(val => {
        this.filtersUpdated.emit(this.filtersForm.value);
      })
    ).subscribe();
  }

  /**
   * Subscribes to function changes
   */
  private _subscribeToFunctionChanges() {
    this.filtersForm.controls['function'].valueChanges.pipe(
      debounceTime(500),
      tap(val => {
        this.filtersUpdated.emit(this.filtersForm.value);
      })
    ).subscribe();
  }

  /**
   * Subscribes to industry changes
   */
  private _subscribeToIndustryChanges() {
    this.filtersForm.controls['industry'].valueChanges.pipe(
      debounceTime(500),
      tap(val => {
        this.filtersUpdated.emit(this.filtersForm.value);
      })
    ).subscribe();
  }

  /**
   * Subscribes to career type changes
   */
  private _subscribeToCareerTypeChanges() {
    this.filtersForm.controls['careerType'].valueChanges.pipe(
      debounceTime(500),
      tap(val => {
        this.filtersUpdated.emit(this.filtersForm.value);
      })
    ).subscribe();
  }

  private _subscribeToSeniorityTypeChanges() {
    this.filtersForm.controls['seniorityType'].valueChanges.pipe(
      debounceTime(500),
      tap(val => {
        this.filtersUpdated.emit(this.filtersForm.value);
      })
    ).subscribe();
  }

  /**
   * Subscribes to event type changes
   */
  private _subscribeToEventTypeChanges() {
    this.filtersForm.controls['eventType'].valueChanges.pipe(
      debounceTime(500),
      tap(val => {
        this.filtersUpdated.emit(this.filtersForm.value);
      })
    ).subscribe();
  }

  /**
   * Subscribes to initiative type changes
   */
  private _subscribeToInitiativeTypeChanges() {
    this.filtersForm.controls['initiativeType'].valueChanges.pipe(
      debounceTime(500),
      tap(val => {
        this.filtersUpdated.emit(this.filtersForm.value);
      })
    ).subscribe();
  }
}
